import { ref, computed } from 'vue';
import axios from 'axios';
import { Auth } from '@/capacitors/services/AuthService';
import { useConfigStore } from '@/stores/config';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import type { OutLet } from '@/components/OutletListSelector.vue';
import { clearMerchantData } from '@/utils/device.utils';

// Extract merchant/outlet data fetching and switching logic from Menu.vue
export function useMerchantOutlets() {
    const configStore = useConfigStore();
    const outlets = ref<OutLet[]>([]);

    // Deduplicated merchant lookup: recalculates when outlets changes
    const currentMerchant = computed(() => {
        const merchantUid = secureStorage.getItem('merchant_uid');
        if (merchantUid) return outlets.value.find(o => o.uid === merchantUid);
    });

    const logoPath = computed(() =>
        configStore.appConfig?.config?.general?.company_logo
        || currentMerchant.value?.logo_image_url
        || import.meta.env.VITE_APP_LOGO
    );

    const merchantName = computed(() => currentMerchant.value?.name || '');

    const loadMerchants = async () => {
        try {
            const token = await Auth.Instance.getValidToken();
            if (token?.accessToken) {
                const instance = axios.create({
                    baseURL: import.meta.env.VITE_BASE_URL,
                    timeout: 20000,
                    headers: { Authorization: 'Bearer ' + token.accessToken },
                });
                const result = await instance.get('/api/v5/merchants');
                if (result.status === 200 && !result.data.error) outlets.value = result.data.data;
            }
        } catch (error) {
            console.error('Failed to get merchants:', error);
        }
    };

    const selectMerchant = async (outlet: OutLet) => {
        try {
            const tokenResponse = localStorage.getItem('cap_sec_token_response');
            if (!tokenResponse) {
                console.error('No token found');
                return;
            }
            // Clear local data before leaving; a clear failure should not block the switch
            await clearMerchantData().catch(e => console.error('Failed to clear merchant data:', e));
            const subdomain = outlet.subdomain ?
                (import.meta.env.DEV ? outlet.subdomain : `https://${outlet.subdomain}.admin.posx.ai`) :
                window.location.origin;
            // Land on /authcallback so the target origin registers the device and re-syncs; strip trailing slash to avoid a "//authcallback" path
            const redirectUrl = `${subdomain.replace(/\/$/, '')}/authcallback?merchant_uid=${outlet.uid}&token=${encodeURIComponent(tokenResponse)}`;
            window.location.href = redirectUrl;
        } catch (error) {
            console.error('Failed to switch merchant:', error);
        }
    };

    return { outlets, merchantName, logoPath, loadMerchants, selectMerchant };
}
